import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ProxyHealthCheckResponse,
  type ProxySummary,
  Routes,
} from '@tg-leads/contracts';

import { api } from '../../lib/api';
import { friendlyError } from '../../lib/friendlyError';

interface ProxyList {
  items: ProxySummary[];
  nextCursor: string | null;
}

const healthBadge = (s: ProxySummary['healthStatus']): string => {
  switch (s) {
    case 'ok':
      return 'badge-ok';
    case 'degraded':
      return 'badge-warn';
    case 'failed':
      return 'badge-fail';
    default:
      return 'badge-neutral';
  }
};

const healthLabel = (s: ProxySummary['healthStatus']): string => {
  switch (s) {
    case 'ok':
      return '正常';
    case 'degraded':
      return '降级';
    case 'failed':
      return '故障';
    default:
      return '未检查';
  }
};

export function ProxiesPage() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['proxies'],
    queryFn: () => api.get<ProxyList>(Routes.admin.proxies),
    // Sweep runs every 60s server-side; UI catches the status flip
    // within one refresh cycle even without clicking "检查".
    refetchInterval: 30_000,
  });

  const healthMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<ProxyHealthCheckResponse>(Routes.admin.proxyHealthCheck(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proxies'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(Routes.admin.proxy(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proxies'] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">代理池</h1>
          <p className="text-sm text-slate-500 mt-1">
            TG 账号通过这些 SOCKS5 代理出口连接 Telegram。密码通过 KMS 加密入库，页面不会以明文回显。
            后台每 60 秒自动跑一次健康检查，状态变化才记审计。
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowAdd(true)}>
          + 添加代理
        </button>
      </div>

      {showAdd && <AddProxyDialog onClose={() => setShowAdd(false)} />}
      {editingId && (
        <EditProxyDialog
          proxy={data!.items.find((p) => p.id === editingId)!}
          onClose={() => setEditingId(null)}
        />
      )}

      <div className="card overflow-x-auto">
        {isLoading ? (
          <div className="text-slate-500 text-sm">加载中…</div>
        ) : error ? (
          <div className="text-red-600 text-sm">{(error as Error).message}</div>
        ) : !data?.items.length ? (
          <div className="text-slate-500 text-sm">
            还没有代理。点右上角"添加代理"开始。
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase border-b">
                <th className="py-2 pr-4">代理地址</th>
                <th className="py-2 pr-4">凭据</th>
                <th className="py-2 pr-4">已绑账号数</th>
                <th className="py-2 pr-4">健康状态</th>
                <th className="py-2 pr-4">最近检查</th>
                <th className="py-2 pr-4">操作</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="py-3 pr-4 font-mono">{p.endpoint}</td>
                  <td className="py-3 pr-4">
                    {p.hasAuth ? (
                      <span className="badge-neutral">已设置</span>
                    ) : (
                      <span className="badge-neutral">无</span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <span
                      className={
                        p.boundAccountCount === 0
                          ? 'text-slate-400'
                          : p.boundAccountCount >= 5
                            ? 'text-orange-600 font-medium'
                            : 'text-slate-700'
                      }
                      title={
                        p.boundAccountCount >= 5
                          ? '建议同一住宅 IP 不超过 5 个账号，避免 TG 风控'
                          : ''
                      }
                    >
                      {p.boundAccountCount}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className={healthBadge(p.healthStatus)}>
                      {healthLabel(p.healthStatus)}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-slate-500 text-xs">
                    {p.lastCheckAt
                      ? new Date(p.lastCheckAt).toLocaleString('zh-CN')
                      : '—'}
                  </td>
                  <td className="py-3 pr-4 space-x-2">
                    <button
                      className="btn-secondary"
                      onClick={() => healthMutation.mutate(p.id)}
                      disabled={healthMutation.isPending}
                    >
                      检查
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => setEditingId(p.id)}
                    >
                      编辑
                    </button>
                    <button
                      className="btn-danger"
                      disabled={p.boundAccountCount > 0 || deleteMutation.isPending}
                      title={
                        p.boundAccountCount > 0
                          ? `已有 ${p.boundAccountCount} 个账号在用，先迁移或删除账号`
                          : ''
                      }
                      onClick={() => {
                        if (confirm(`确认删除 ${p.endpoint}?`)) {
                          deleteMutation.mutate(p.id);
                        }
                      }}
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AddProxyDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [endpoint, setEndpoint] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<ProxySummary>(Routes.admin.proxies, {
        endpoint,
        username: username || undefined,
        password: password || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proxies'] });
      onClose();
    },
    onError: (e: unknown) => {
      setErr(friendlyError(e));
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    mutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <form
        onSubmit={onSubmit}
        className="card w-96 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">添加 SOCKS5 代理</h2>
        <div>
          <label className="label">代理地址（IP:端口）</label>
          <input
            className="input"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            placeholder="1.2.3.4:1080"
            required
            pattern="[^:\s]+:\d{1,5}"
          />
        </div>
        <div>
          <label className="label">用户名（可选）</label>
          <input
            className="input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <label className="label">密码（可选）</label>
          <input
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        {err && (
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
            {err}
          </div>
        )}
        <div className="flex justify-end space-x-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            取消
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? '提交中…' : '保存'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Edit an existing proxy. Endpoint is pre-filled; credentials are NOT
 * (the API never returns them). User leaves both username + password
 * blank → server keeps existing creds. Filling either → both replaced
 * (per server contract — partial cred updates are too ambiguous).
 */
function EditProxyDialog({
  proxy,
  onClose,
}: {
  proxy: ProxySummary;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [endpoint, setEndpoint] = useState(proxy.endpoint);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [replaceCreds, setReplaceCreds] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const body: { endpoint?: string; username?: string; password?: string } = {};
      if (endpoint && endpoint !== proxy.endpoint) body.endpoint = endpoint;
      if (replaceCreds) {
        body.username = username || undefined;
        body.password = password || undefined;
      }
      return api.put<ProxySummary>(Routes.admin.proxy(proxy.id), body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proxies'] });
      onClose();
    },
    onError: (e: unknown) => {
      setErr(friendlyError(e));
    },
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          mutation.mutate();
        }}
        className="card w-96 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">编辑代理</h2>
        <div>
          <label className="label">代理地址（IP:端口）</label>
          <input
            className="input"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            placeholder="1.2.3.4:1080"
            required
            pattern="[^:\s]+:\d{1,5}"
          />
        </div>
        <div className="border-t pt-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={replaceCreds}
              onChange={(e) => setReplaceCreds(e.target.checked)}
            />
            替换账号密码
            <span className="text-xs text-slate-400">
              {proxy.hasAuth ? '（当前已设置）' : '（当前无）'}
            </span>
          </label>
          {replaceCreds && (
            <div className="space-y-3 mt-3">
              <div>
                <label className="label">新用户名（留空 = 无）</label>
                <input
                  className="input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="label">新密码（留空 = 无）</label>
                <input
                  type="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <p className="text-[10px] text-slate-400">
                注意：服务端逻辑是"动凭据就两个一起换"。不勾选这个就完全不动凭据。
              </p>
            </div>
          )}
        </div>
        {err && (
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
            {err}
          </div>
        )}
        <div className="flex justify-end space-x-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            取消
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? '保存中…' : '保存'}
          </button>
        </div>
      </form>
    </div>
  );
}
